import React from "react";
import { useNavigate } from "react-router-dom";
import { AI_LAB_COURSES } from "../../constants/ai-lab-constants";
import { Button } from "../../../../components";
import { Sparkles, Clock, ArrowRight } from "lucide-react";

const AiLabIntro = () => {
  const navigate = useNavigate();

  // Helper to map course description based on course id
  const getCourseDescription = (id) => {
    switch (id) {
      case "ai-robotics":
        return "Learn AI fundamentals, machine learning basics, and build smart robots.";
      case "coding":
        return "Python, JavaScript, and Scratch programming for young learners.";
      case "design":
        return "UI/UX design, Figma, and creative digital design thinking.";
      case "video-editing":
        return "Video creation, editing techniques, and visual storytelling.";
      case "aerodynamics":
        return "Aeronautics, drone building, and flight programming.";
      case "sketching":
        return "Digital art, character design, and creative visualization.";
      case "3d-modelling":
        return "3D design, modeling software, and printing technology.";
      case "smart-money":
        return "Financial literacy, investing basics, and entrepreneurship.";
      default:
        return "Comprehensive program designed to build future-ready skills.";
    }
  };

  return (
    <div className="space-y-12 max-w-5xl mx-auto text-left py-4">
      {/* Hero Section */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-50 via-indigo-50/30 to-purple-50/50 border border-blue-100 p-8 sm:p-10 shadow-xs">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/40 rounded-full blur-3xl pointer-events-none" />
        
        <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-8">
          <div className="space-y-4 max-w-2xl">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white text-blue-700 text-xs font-bold border border-blue-150">
              <Sparkles className="w-3.5 h-3.5 text-amber-500 animate-pulse" />
              <span>Practido AI LAB Program</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight leading-tight">
              Future Skills <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Start Here.</span>
            </h2>
            
            <p className="text-sm sm:text-base text-gray-650 font-medium leading-relaxed">
              Learn AI, Coding, Robotics, Game Development, and Future Technologies with India's leading AI education platform. Select from 8 comprehensive programs tailored to class-appropriate difficulty levels.
            </p>

            <div className="pt-2 flex flex-wrap items-center gap-3">
              <Button
                version="secondary"
                text="Fill Enrolment Form"
                handleClick={() => navigate("/dashboard/ai-lab/enrol")}
              >
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
              <button
                type="button"
                onClick={() => navigate("/dashboard?type=book-robotics-lab-demo")}
                className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-black text-xs rounded-xl shadow-sm transition flex items-center gap-1.5 cursor-pointer"
              >
                <Sparkles className="w-3.5 h-3.5 text-amber-300 animate-pulse" />
                Book Free Demo
              </button>
            </div>
          </div>

          {/* Quick stats panel */}
          <div className="grid grid-cols-2 gap-4 lg:w-96 flex-shrink-0">
            <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-2xs">
              <div className="text-xs text-gray-400 font-bold uppercase tracking-wider">Success Rate</div>
              <div className="text-2xl font-black text-blue-650 mt-1">98%</div>
              <div className="text-[11px] text-gray-500 mt-0.5">Project completion</div>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-2xs">
              <div className="text-xs text-gray-400 font-bold uppercase tracking-wider">Students</div>
              <div className="text-2xl font-black text-purple-655 mt-1">20K+</div>
              <div className="text-[11px] text-gray-500 mt-0.5">Active learners</div>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-2xs">
              <div className="text-xs text-gray-400 font-bold uppercase tracking-wider">Classes</div>
              <div className="text-2xl font-black text-emerald-650 mt-1">Live</div>
              <div className="text-[11px] text-gray-500 mt-0.5">Expert-led interactive</div>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-2xs">
              <div className="text-xs text-gray-400 font-bold uppercase tracking-wider">Learning</div>
              <div className="text-2xl font-black text-amber-600 mt-1">Project</div>
              <div className="text-[11px] text-gray-500 mt-0.5">Hands-on building</div>
            </div>
          </div>
        </div>
      </div>

      {/* 4-Step Process Section */}
      <div className="space-y-6">
        <div className="text-center md:text-left space-y-1.5">
          <h3 className="text-xl font-bold text-gray-900">How to Enrol</h3>
          <p className="text-xs text-gray-500">Get your child started with our simple 4-step program onboarding process.</p>
        </div>

        <div className="flex flex-col lg:flex-row items-center lg:items-stretch justify-between gap-4 lg:gap-3">
          {/* Step 1 card */}
          <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-2xs relative flex flex-col justify-between min-h-[140px] w-full lg:flex-1">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-2xl">👧</span>
                <span className="font-black text-xs text-gray-300">STEP 01</span>
              </div>
              <h4 className="font-extrabold text-sm text-gray-850">Child Details</h4>
              <p className="text-[11px] text-gray-500 leading-relaxed">Provide student details (Name, Class grade, and optional school name).</p>
            </div>
          </div>

          {/* Arrow 1 */}
          <div className="flex items-center justify-center text-gray-300 py-1 lg:py-0">
            <ArrowRight className="w-5 h-5 rotate-90 lg:rotate-0" />
          </div>

          {/* Step 2 card */}
          <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-2xs relative flex flex-col justify-between min-h-[140px] w-full lg:flex-1">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-2xl">📚</span>
                <span className="font-black text-xs text-gray-300">STEP 02</span>
              </div>
              <h4 className="font-extrabold text-sm text-gray-850">Select Courses</h4>
              <p className="text-[11px] text-gray-500 leading-relaxed">Choose appropriate courses per child. Price scales based on student grade.</p>
            </div>
          </div>

          {/* Arrow 2 */}
          <div className="flex items-center justify-center text-gray-300 py-1 lg:py-0">
            <ArrowRight className="w-5 h-5 rotate-90 lg:rotate-0" />
          </div>

          {/* Step 3 card */}
          <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-2xs relative flex flex-col justify-between min-h-[140px] w-full lg:flex-1">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-2xl">📅</span>
                <span className="font-black text-xs text-gray-300">STEP 03</span>
              </div>
              <h4 className="font-extrabold text-sm text-gray-850">Pick Schedule</h4>
              <p className="text-[11px] text-gray-500 leading-relaxed">Select matching weekday batch and convenient live timing slot preference.</p>
            </div>
          </div>

          {/* Arrow 3 */}
          <div className="flex items-center justify-center text-gray-300 py-1 lg:py-0">
            <ArrowRight className="w-5 h-5 rotate-90 lg:rotate-0" />
          </div>

          {/* Step 4 card */}
          <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-2xs relative flex flex-col justify-between min-h-[140px] w-full lg:flex-1">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-2xl">💳</span>
                <span className="font-black text-xs text-gray-300">STEP 04</span>
              </div>
              <h4 className="font-extrabold text-sm text-gray-850">Payment & Review</h4>
              <p className="text-[11px] text-gray-500 leading-relaxed">Review breakdown items in the summary checkout cart and pay securely.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Courses Offer Grid */}
      <div className="space-y-6">
        <div className="text-center md:text-left space-y-1.5">
          <h3 className="text-xl font-bold text-gray-900">Programs We Offer</h3>
          <p className="text-xs text-gray-500">Explore the 8 future technology topics available in the AI Lab curriculum.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {AI_LAB_COURSES.map((course) => (
            <div
              key={course.id}
              style={{
                backgroundColor: course.bg,
                borderColor: course.border,
              }}
              className="group p-5 rounded-2xl border flex flex-col justify-between min-h-[185px] h-full shadow-3xs transition-all duration-200 hover:shadow-2xs"
            >
              <div className="space-y-2">
                <span className="text-2xl block group-hover:scale-110 transition-transform duration-200 w-fit">{course.icon}</span>
                <h4 className="font-extrabold text-xs text-gray-900 tracking-tight leading-snug">{course.label}</h4>
                <p className="text-[10px] text-gray-650 leading-relaxed font-semibold">{getCourseDescription(course.id)}</p>
              </div>
              
              <div className="flex items-center justify-between border-t border-black/5 pt-2 mt-2">
                <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Live Batch
                </span>
                <span className="text-[9px] font-black bg-white px-2 py-0.5 rounded-full border border-black/5 text-gray-800 shadow-3xs">
                  Class 1–12
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom CTA Block */}
      <div className="bg-[#FAF8F5] border border-gray-150 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-6">
        <div className="space-y-1 text-center sm:text-left">
          <h4 className="font-extrabold text-base text-gray-900">Ready to build future skills?</h4>
          <p className="text-xs text-gray-500 font-semibold">Start the dynamic enrolment form to secure your child's live online batch classes.</p>
        </div>
        <div className="flex-shrink-0 flex flex-wrap items-center gap-3">
          <Button
            version="secondary"
            text="Fill Enrolment Form"
            handleClick={() => navigate("/dashboard/ai-lab/enrol")}
          >
            <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
          <button
            type="button"
            onClick={() => navigate("/dashboard?type=book-robotics-lab-demo")}
            className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-black text-xs rounded-xl shadow-sm transition flex items-center gap-1.5 cursor-pointer"
          >
            <Sparkles className="w-3.5 h-3.5 text-amber-300 animate-pulse" />
            Book Free Demo
          </button>
        </div>
      </div>
    </div>
  );
};

export default AiLabIntro;
